import { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { Link } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { ChevronDown, Zap, CheckCircle2 } from 'lucide-react'
import { cn } from '@/utils/cn'
import { useAppTheme } from '@/design-system/island'
import '@/design-system/shell.css'

/** The narrowest the panel may sit from the edge of the page. */
const GUTTER = 16

interface ResourcesDropdownProps {
  className?: string
}

/*
  This menu hangs off two different headers: the application's, which has
  converted, and the pillar pages', which has not. So it carries its own `.ds`
  scope rather than borrowing one, and reads the theme the same way DsIsland
  does. DsIsland itself takes no ref, and the outside-click handler needs one.
*/
export default function ResourcesDropdown({ className }: ResourcesDropdownProps) {
  const { isSignedIn } = useUser()
  const theme = useAppTheme()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [shift, setShift] = useState(0)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  /*
    The panel is 680px wide and hangs off the trigger's right edge, and the
    trigger sits in the middle of a nav. On a wide screen there is room to its
    left; below roughly 1050px there is not, and the panel ran off the side of
    the page. Both navs appear from 768px up, so that band is real.

    CSS cannot ask how far the trigger is from the left edge, so this measures
    it and nudges the panel back by however much it would have overflowed.
    `offsetWidth` ignores the transform, so the measurement stays stable across
    renders instead of chasing its own correction.
  */
  useLayoutEffect(() => {
    if (!isOpen) return

    const fit = () => {
      const wrap = dropdownRef.current
      const panel = panelRef.current
      if (!wrap || !panel) return
      const panelLeft = wrap.getBoundingClientRect().right - panel.offsetWidth
      setShift(Math.max(0, GUTTER - panelLeft))
    }

    fit()
    window.addEventListener('resize', fit)

    /*
      `resize` alone leaves the correction stale when the trigger moves without
      the window changing, which the header does: the plan chip and the team
      name both arrive after their queries settle. Watching the page and the
      trigger catches that too.
    */
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit)
    observer?.observe(document.documentElement)
    if (dropdownRef.current) observer?.observe(dropdownRef.current)

    return () => {
      window.removeEventListener('resize', fit)
      observer?.disconnect()
    }
  }, [isOpen])

  const learningResources = [
    { name: 'AEO Guide', path: '/aeo', description: 'Answer Engine Optimization complete guide' },
    { name: 'GEO Guide', path: '/geo', description: 'Generative Engine Optimization - be the source AI chooses' },
    { name: 'AI Search Optimization', path: '/ai-search-optimization', description: 'Get cited by AI engines (SGE, Copilot, Perplexity)' },
    { name: 'Schema Markup Guide', path: '/schema-markup', description: 'What, why, and how of structured data' },
    { name: 'Improve Schema Quality Score', path: '/schema-markup/improve-quality-score', description: 'Copy-paste snippets to boost your schema score' }
  ]

  const schemaGenerators = [
    { name: 'FAQ', path: '/faq-schema-generator' },
    { name: 'Article', path: '/article-schema-generator' },
    { name: 'BlogPosting', path: '/blogposting-schema-generator' },
    { name: 'HowTo', path: '/howto-schema-generator' },
    { name: 'Product', path: '/product-schema-generator' },
    { name: 'LocalBusiness', path: '/localbusiness-schema-generator' },
    { name: 'Organization', path: '/organization-schema-generator' },
    { name: 'Event', path: '/event-schema-generator' },
    { name: 'Review', path: '/review-schema-generator' },
    { name: 'Breadcrumb', path: '/breadcrumb-schema-generator' }
  ]

  const tools = [
    { name: 'Generate Schema', path: '/dashboard', icon: Zap },
    { name: 'Schema Markup Grader', path: '/schema-markup-grader', icon: CheckCircle2, requiresAuth: false }
  ]

  return (
    <div ref={dropdownRef} className={cn('ds ds-res', className)} data-ds-theme={theme}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="ds-trigger"
      >
        Resources
        <ChevronDown className={cn('transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div
          ref={panelRef}
          className="ds-res__panel"
          style={shift ? { transform: `translateX(${shift}px)` } : undefined}
        >
          <div className="ds-res__groups">
            <section>
              <h3 className="ds-res__grouphead">Learning Hub</h3>
              <div className="ds-res__guides">
                {learningResources.map((resource, index) => (
                  <Link
                    key={index}
                    to={resource.path}
                    onClick={() => setIsOpen(false)}
                    className="ds-res__item"
                  >
                    <span className="ds-res__what">
                      {resource.name}
                    </span>
                    <span className="ds-res__why">
                      {resource.description}
                    </span>
                  </Link>
                ))}
              </div>
            </section>

            <section>
              <h3 className="ds-res__grouphead">Free Schema Generators</h3>
              <div className="ds-res__gens">
                {schemaGenerators.map((generator, index) => (
                  <Link
                    key={index}
                    to={generator.path}
                    onClick={() => setIsOpen(false)}
                    className="ds-res__gen"
                  >
                    {generator.name}
                  </Link>
                ))}
              </div>
            </section>
          </div>

          <div className="ds-res__foot">
            {tools.map((tool, index) => {
              const Icon = tool.icon
              // If tool doesn't require auth, always use its path. Otherwise, redirect to sign-up if not signed in
              const toolPath = (tool.requiresAuth === false || isSignedIn) ? tool.path : '/sign-up'
              return (
                <Link
                  key={index}
                  to={toolPath}
                  onClick={() => setIsOpen(false)}
                  className="ds-res__cta"
                >
                  <Icon aria-hidden="true" />
                  <span>{tool.name}</span>
                </Link>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
